import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../../components/common/data-table/DataTable";
import Filters from "../filters/Filters";
import styles from "./AuditResultTable.module.scss";
import AuditService, {
  AuditFilterParams,
} from "../../../api/services/audit.service";
import { AuditFiltersState } from "../Audit";
import { useCurrencies } from "../../../context/CurrenciesContext";

interface Props {
  filters: AuditFiltersState;
  storeId: string;
  startDate: string;
  endDate: string;
  setStoreId: (v: string, name?: string) => void;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  onApply: () => void;
  onReset: () => void;
}

interface PayoutRow {
  store_id: number;
  store_name: string;
  gmv: number;
  platform_profit: number;
  tax: number;
  platform_margin_net: number;
  platform_margin_correct: number;
  store_payout: number;
  order_count: number;
  paid_to_store: number;
  not_yet_paid: number;
  store_remaining: number;
}

const AuditResultTable = ({
  filters,
  storeId,
  startDate,
  endDate,
  setStoreId,
  setStartDate,
  setEndDate,
  onApply,
  onReset,
}: Props) => {
  const { t } = useTranslation("translation");
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [loading, setLoading] = useState(false);
  const { currencyId, currency } = useCurrencies();

  const formatMoney = (val?: number) =>
    `${(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`;

  const columns: Column[] = useMemo(
    () => [
      { key: "store_name", header: t("audit_results.table.store") as string },
      {
        key: "gmv",
        header: t("audit_results.table.gmv") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "platform_profit",
        header: t("audit_results.table.platform_profit") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "tax",
        header: t("audit_results.table.tax") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "platform_margin_net",
        header: t("audit_results.table.platform_margin_net") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "platform_margin_correct",
        header: t("audit_results.table.platform_margin_correct") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "store_payout",
        header: t("audit_results.table.store_payout") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "paid_to_store",
        header: t("audit_results.table.paid_to_store") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "not_yet_paid",
        header: t("audit_results.table.not_yet_paid") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "store_remaining",
        header: t("audit_results.table.store_remaining") as string,
        render: (v) => formatMoney(v),
      },
      {
        key: "order_count",
        header: t("audit_results.table.orders") as string,
      },
    ],
    [currency, t],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: AuditFilterParams = {};
        if (filters.startDate) params.start_date = filters.startDate;
        if (filters.endDate) params.end_date = filters.endDate;
        if (filters.storeId) params.store_id = filters.storeId;
        if (currencyId) params.currency_id = currencyId;
        const res = await AuditService.getPayoutReport(params);
        setRows(res.data?.data || []);
      } catch (e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, currencyId]);

  return (
    <div>
      <Filters
        storeId={storeId}
        startDate={startDate}
        endDate={endDate}
        onChangeStore={setStoreId}
        onChangeStartDate={setStartDate}
        onChangeEndDate={setEndDate}
        onApply={onApply}
        onReset={onReset}
      />
      <DataTable
        table_title={t("audit_results.title") as string}
        data={rows}
        columns={columns}
        other
        loading={loading}
      />
    </div>
  );
};

export default AuditResultTable;
